import type { Controller } from '../../core/domain/controller';
import type { StreamFrame } from '../../core/bridge/types';
import type { BackendClient } from '../bridge/backend-client';

/*
 * ControllerManager — main-thread PROXY of the canonical manager that lives in
 * the Backend Worker. It opens one streaming subscription (a MessagePort),
 * maintains a local cache from snapshot + deltas, and re-emits `change` events.
 * Lit components subscribe to it via `addEventListener('change', …)` — exactly
 * the manager-pattern shape components already know, with the real IO + state
 * one OS thread away. See docs/nats-frontend-architecture.md.
 */
/** Tri-state data-plane liveness for the gateway badge — never a fake
 *  "connected". `connecting` (yellow) while we await the first snapshot,
 *  `connected` (green) once data is genuinely flowing, `disconnected` (gray)
 *  after a stream error. Maps 1:1 to statusColor()/statusText(). */
// 'offline' = the FLEET says the box is powered off / its leaf is gone. Distinct
// from 'disconnected' (the USER detached us) and from 'connecting' (we are mid-
// reconnect and genuinely don't know). Only deriveConnState produces it; the
// data-plane managers below never do — they cannot see the box, only their stream.
export type ConnState = 'connecting' | 'connected' | 'disconnected' | 'offline';

export class ControllerManager extends EventTarget {
  private readonly cache = new Map<string, Controller>();
  private port?: MessagePort;
  // Starts 'connecting': the manager is created only to immediately subscribe,
  // so the honest initial state is "establishing", not "disconnected".
  private connState: ConnState = 'connecting';

  constructor(
    private readonly backend: BackendClient,
    private readonly gatewayId: string,
  ) {
    super();
  }

  get all(): Controller[] {
    return [...this.cache.values()];
  }

  /** AUTHORITATIVE data-plane liveness: 'connected' ONLY once the gateway's
   *  controller stream has delivered a NETWORK snapshot (api-gateway + the gateway's KV
   *  genuinely reachable); 'connecting' before the first frame; 'disconnected'
   *  after a stream error. The single source of truth for the gateway badge. */
  get connectionState(): ConnState {
    return this.connState;
  }

  async start(): Promise<void> {
    // A fresh subscribe is 'establishing' — drop any stale cache + state so a
    // RE-start (reconnect after a frontend disconnect) honestly shows 'connecting'
    // until the new snapshot, instead of lingering on the prior 'connected'.
    this.connState = 'connecting';
    this.cache.clear();
    this.dispatchEvent(new CustomEvent('change'));
    this.port = await this.backend.api.subscribeControllers(this.gatewayId);
    this.port.onmessage = (e: MessageEvent<StreamFrame<Controller>>) => this.onFrame(e.data);
    this.port.start();
  }

  stop(): void {
    this.port?.postMessage({ kind: 'close' });
    this.port = undefined;
    this.cache.clear(); // a stopped manager holds no live data (frontend disconnect)
  }

  private onFrame(frame: StreamFrame<Controller>): void {
    switch (frame.kind) {
      case 'snapshot':
        // Revision 0 is the worker's boot-from-cache frame — last session's list,
        // painted before the network has answered. Show it; do not let it vouch
        // for a live data plane, or the badge goes green on a gateway we have not
        // reached this session at all.
        if (frame.revision > 0) this.connState = 'connected';
        this.cache.clear();
        for (const c of frame.items) this.cache.set(c.id, c);
        break;
      case 'delta':
        if (frame.op === 'remove') this.cache.delete(frame.item.id);
        else this.cache.set(frame.item.id, frame.item);
        break;
      case 'error':
        // Stream broke → the data plane is down (deriveConnState renders this as
        // 'connecting': the worker auto-reconnects forever, so it is never a user-
        // facing "disconnected"). KEEP the cache: a transient blip — a broker
        // restart, a NATS reconnect, a leg switch — must NOT blank the controller
        // list for the 400ms-12s reconnect. The next snapshot replaces the set
        // wholesale, so a stale entry can never outlive a recovery. Same
        // keep-last-known rule as ModelManager + the app-shell fleet handler.
        this.connState = 'disconnected';
        this.dispatchEvent(new CustomEvent('change'));
        return;
    }
    this.dispatchEvent(new CustomEvent('change'));
  }
}
